import SiteRegistrationProps from "@mediaspace/features/registration/registration/SiteRegistrationProps";
import { forwardRef, useState } from "react";
import { Drawer } from "@kaltura/mediaspace-shared-ui";
import { Button, ButtonProps } from "@kaltura/ds-react-components";
import SiteRegistrationDrawerContainer from "./site-registration-drawer-container/SiteRegistrationDrawerContainer";

export interface RegistrationButtonProps extends ButtonProps {
    /**
     * props for registration form
     */
    siteRegistrationProps: SiteRegistrationProps;

    /**
     * open registration form drawer on start
     */
    isRegistrationFormOpen?: boolean;

    /**
     * callback invoked on cancel exit confirmation
     */
    onConfirmationCancel?: () => void;

    /**
     * callback invoked on confirm exit confirmation
     */
    onConfirmationConfirmExit?: () => void;

    /**
     * callback invoked on registration form drawer open
     */
    onRegistrationFormOpen?: () => void;

    /**
     * callback invoked on registration form drawer close
     */
    onRegistrationFormClose?: () => void;
}

export const RegistrationButton = forwardRef<any, RegistrationButtonProps>(
    (
        {
            siteRegistrationProps,
            children,
            onConfirmationCancel,
            onConfirmationConfirmExit,
            onRegistrationFormOpen,
            onRegistrationFormClose,
            isRegistrationFormOpen = false,
            ...props
        }: RegistrationButtonProps,
        ref
    ) => {
        const [open, setOpen] = useState(isRegistrationFormOpen);
        const [shouldClose, setShouldClose] = useState(false);
        const [registrationCompleted, setRegistrationCompleted] = useState(false);

        // assign onRegistrationCompleted callback to siteRegistrationProps
        siteRegistrationProps.onRegistrationCompleted = () => {
            setRegistrationCompleted(true);
        };

        const handleOnRegistrationClose = () => {
            // actually close
            setOpen(false);
            // reset
            setShouldClose(false);
            if (onConfirmationConfirmExit) {
                onConfirmationConfirmExit();
            }
        };

        const handleOnRegistrationCancel = () => {
            // reset
            setShouldClose(false);
            if (onConfirmationCancel) {
                onConfirmationCancel();
            }
        };

        const openButtonClickHandler = () => {
            setOpen(true);
            //reset registration completed state
            setRegistrationCompleted(false);
            if (onRegistrationFormOpen) {
                onRegistrationFormOpen();
            }
        };

        const drawerOnCloseHandler = () => {
            // set to show exit confirmation if registration not completed,
            // otherwise reset it's value
            setShouldClose(!registrationCompleted);

            if (registrationCompleted) {
                // registration completed, close the drawer
                setOpen(false);
            }

            if (onRegistrationFormClose) {
                onRegistrationFormClose();
            }
        };

        return (
            <>
                <Button ref={ref} onClick={openButtonClickHandler} {...props}>
                    {children}
                </Button>
                <Drawer
                    contentContainerVariant={"fullWidth"}
                    classes={{
                        root: "kms-ds-site-registration-drawer-root",
                        paper: "kms-ds-site-registration-drawer-paper",
                    }}
                    open={open}
                    onClose={drawerOnCloseHandler}
                    maxWidth={687}
                >
                    <SiteRegistrationDrawerContainer
                        siteRegistrationProps={siteRegistrationProps}
                        shouldClose={shouldClose}
                        onClose={handleOnRegistrationClose}
                        onCancel={handleOnRegistrationCancel}
                    />
                </Drawer>
            </>
        );
    }
);
